const TOOLTIP_ID = 'taskelloTooltip';
const TOOLTIP_ATTR = 'data-tooltip-content';
const TOOLTIP_INIT_ATTR = 'data-tooltip-init';
const TOOLTIP_OFFSET = 12;
const TOOLTIP_VIEWPORT_MARGIN = 12;

class TooltipController {
    constructor() {
        this.tooltip = null;
        this.activeElement = null;
        this.hideTimer = null;

        this.handleMouseOver = this.handleMouseOver.bind(this);
        this.handleMouseOut = this.handleMouseOut.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);

        this.initExisting(document);
        this.observeMutations();
        this.bindEvents();
        this.log('Tooltip controller initialized');
    }

    log(...args) {
        window.Taskello?.debug?.log?.('[tooltip]', ...args);
    }

    bindEvents() {
        document.addEventListener('mouseover', this.handleMouseOver);
        document.addEventListener('mouseout', this.handleMouseOut);
        document.addEventListener('focusin', this.handleFocusIn);
        document.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('pointerdown', this.handlePointerDown);
        window.addEventListener('scroll', this.handleViewportChange, true);
        window.addEventListener('resize', this.handleViewportChange);
    }

    observeMutations() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
                    this.prepareElement(mutation.target);
                    return;
                }

                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) {
                        return;
                    }

                    this.initExisting(node);
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['title', 'data-tooltip-content']
        });
    }

    initExisting(root) {
        if (!(root instanceof HTMLElement) && root !== document) {
            return;
        }

        if (root instanceof HTMLElement) {
            this.prepareElement(root);
        }

        const scope = root === document ? document : root;
        scope.querySelectorAll?.('[title], [data-tooltip-content]').forEach((element) => this.prepareElement(element));
    }

    prepareElement(element) {
        if (!(element instanceof HTMLElement)) {
            return;
        }

        const explicitTooltip = element.getAttribute(TOOLTIP_ATTR);
        const title = element.getAttribute('title');
        const normalizedExplicitTooltip = explicitTooltip?.trim() || '';
        const normalizedTitle = title?.trim() || '';
        const isInitialized = element.getAttribute(TOOLTIP_INIT_ATTR) === 'true';

        if (normalizedExplicitTooltip !== '') {
            if (isInitialized && explicitTooltip === normalizedExplicitTooltip && normalizedTitle === '') {
                return;
            }

            if (explicitTooltip !== normalizedExplicitTooltip) {
                element.setAttribute(TOOLTIP_ATTR, normalizedExplicitTooltip);
            }

            if (!isInitialized) {
                element.setAttribute(TOOLTIP_INIT_ATTR, 'true');
            }

            this.log('Registered explicit tooltip content', {
                element,
                content: normalizedExplicitTooltip
            });

            if (normalizedTitle !== '') {
                element.removeAttribute('title');
                this.log('Removed native title in favor of explicit tooltip', element);
            }

            return;
        }

        if (normalizedTitle === '') {
            return;
        }

        element.setAttribute(TOOLTIP_ATTR, normalizedTitle);

        if (!isInitialized) {
            element.setAttribute(TOOLTIP_INIT_ATTR, 'true');
        }

        element.removeAttribute('title');
        this.log('Migrated native title to tooltip content', {
            element,
            content: normalizedTitle
        });
    }

    getTooltipElement() {
        if (this.tooltip?.isConnected) {
            return this.tooltip;
        }

        if (this.tooltip && !this.tooltip.isConnected) {
            this.log('Recreating detached tooltip element');
            this.tooltip = null;
        }

        const tooltip = document.createElement('div');
        tooltip.id = TOOLTIP_ID;
        tooltip.className = 'taskello-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.setAttribute('aria-hidden', 'true');
        document.body.appendChild(tooltip);

        this.tooltip = tooltip;
        this.log('Created tooltip element');
        return tooltip;
    }

    getTrigger(target) {
        if (!(target instanceof HTMLElement)) {
            return null;
        }

        const trigger = target.closest(`[${TOOLTIP_ATTR}]`);

        if (!(trigger instanceof HTMLElement)) {
            return null;
        }

        const content = trigger.getAttribute(TOOLTIP_ATTR);
        return content ? trigger : null;
    }

    showFor(element) {
        const content = element.getAttribute(TOOLTIP_ATTR);

        if (!content) {
            return;
        }

        window.clearTimeout(this.hideTimer);
        this.activeElement = element;

        const tooltip = this.getTooltipElement();
        tooltip.textContent = content;
        tooltip.setAttribute('aria-hidden', 'false');
        tooltip.classList.add('is-visible');
        this.log('Showing tooltip', {
            element,
            content
        });
        this.positionTooltip(element);
    }

    hide(immediate = false) {
        const tooltip = this.tooltip;

        if (!tooltip) {
            this.activeElement = null;
            return;
        }

        const applyHide = () => {
            tooltip.classList.remove('is-visible');
            tooltip.setAttribute('aria-hidden', 'true');
            this.activeElement = null;
            this.log('Tooltip hidden');
        };

        window.clearTimeout(this.hideTimer);

        if (immediate) {
            applyHide();
            return;
        }

        this.hideTimer = window.setTimeout(applyHide, 40);
    }

    positionTooltip(element) {
        const tooltip = this.tooltip;

        if (!tooltip || !element) {
            return;
        }

        const rect = element.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
        left = Math.max(TOOLTIP_VIEWPORT_MARGIN, Math.min(left, viewportWidth - tooltipRect.width - TOOLTIP_VIEWPORT_MARGIN));

        let top = rect.top - tooltipRect.height - TOOLTIP_OFFSET;

        if (top < TOOLTIP_VIEWPORT_MARGIN) {
            top = rect.bottom + TOOLTIP_OFFSET;
        }

        if (top + tooltipRect.height > viewportHeight - TOOLTIP_VIEWPORT_MARGIN) {
            top = Math.max(TOOLTIP_VIEWPORT_MARGIN, viewportHeight - tooltipRect.height - TOOLTIP_VIEWPORT_MARGIN);
        }

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        this.log('Positioned tooltip', {
            element,
            left,
            top
        });
    }

    handleMouseOver(event) {
        const trigger = this.getTrigger(event.target);

        if (!trigger || trigger === this.activeElement) {
            return;
        }

        this.showFor(trigger);
    }

    handleMouseOut(event) {
        if (!this.activeElement) {
            return;
        }

        const relatedTarget = event.relatedTarget;

        if (relatedTarget instanceof Node && this.activeElement.contains(relatedTarget)) {
            return;
        }

        if (event.target instanceof Node && this.activeElement.contains(event.target)) {
            this.hide();
        }
    }

    handleFocusIn(event) {
        const trigger = this.getTrigger(event.target);

        if (!trigger) {
            return;
        }

        this.showFor(trigger);
    }

    handleFocusOut(event) {
        if (!this.activeElement) {
            return;
        }

        const relatedTarget = event.relatedTarget;

        if (relatedTarget instanceof Node && this.activeElement.contains(relatedTarget)) {
            return;
        }

        this.hide();
    }

    handleViewportChange() {
        if (this.activeElement) {
            this.log('Repositioning active tooltip after viewport change');
            this.positionTooltip(this.activeElement);
        }
    }

    handleKeyDown(event) {
        if (event.key === 'Escape') {
            this.hide(true);
        }
    }

    handlePointerDown() {
        this.hide(true);
    }
}

const initTooltips = () => {
    if (!document.body) {
        return;
    }

    if (!window.taskello) {
        window.taskello = {};
    }

    if (!window.taskello.tooltipController) {
        window.taskello.tooltipController = new TooltipController();
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTooltips, { once: true });
} else {
    initTooltips();
}
